import {
  SiAtlassian,
  SiGithub,
  SiGitlab,
  SiGoogle,
  SiLinear,
  SiPinterest,
  SiToggl,
  SiTrello,
  SiYcombinator,
} from "@icons-pack/react-simple-icons";
import { CheckCheckIcon, NotebookIcon } from "lucide-react";
import { createElement, type ComponentType } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Bitrix24Icon } from "@/components/Icons/Bitrix24Icon";
import { Elements } from "@/components/elements/Elements";
import { Element } from "@/components/elements/UIElement";
import { TemplateSnapshotElement } from "@/lib/types";

type WidgetBreakdown = {
  name: string;
  count: number;
};

type ThumbnailWidget = {
  name: string;
  x: number | null;
  y: number | null;
  w: number;
  h: number;
  providerKey: string | null;
  order: number;
};

const LAYOUT_ELEMENT_NAMES = new Set([
  "Root",
  "App",
  "ProfileSelector",
  "Grid",
  "Row",
  "Column",
  "Search",
  "Greeting",
]);

// Elements that are structural chrome and never counted as widgets in a
// template thumbnail. (Formerly held WallpaperSwitcher, now removed.)
const BASE_ELEMENT_NAMES = new Set<string>([]);

const PROVIDER_ALIASES: Record<string, string> = {
  github: "github",
  githubpersonal: "github",
  gitlab: "gitlab",
  google: "google",
  trello: "trello",
  atlassian: "atlassian",
  jira: "atlassian",
  linear: "linear",
  pinterest: "pinterest",
  bitrix24: "bitrix24",
  toggl: "toggl",
  internalnote: "internalnote",
  internaltodo: "internaltodo",
  hackernews: "hackernews",
  quotes: "unideck",
};

const PROVIDER_ICON_META: Record<string, { plate: string; icon: string }> = {
  github: { plate: "#111827", icon: "#f9fafb" },
  gitlab: { plate: "#ffffffee", icon: "#fc6d26" },
  google: { plate: "#ffffffee", icon: "#4285f4" },
  trello: { plate: "#ffffffee", icon: "#0ea5e9" },
  atlassian: { plate: "#ffffffee", icon: "#0052cc" },
  linear: { plate: "#1f2937", icon: "#f5f3ff" },
  pinterest: { plate: "#ffffffee", icon: "#e60023" },
  bitrix24: { plate: "#ffffffee", icon: "#0075ff" },
  toggl: { plate: "#ffffffee", icon: "#e57cd8" },
  internalnote: { plate: "#111827", icon: "#fde047" },
  internaltodo: { plate: "#111827", icon: "#34d399" },
  hackernews: { plate: "#ffffffee", icon: "#f97316" },
  unideck: { plate: "#ffffffee", icon: "#38bdf8" },
};

const PROVIDER_ICON_COMPONENTS: Record<string, ComponentType<any>> = {
  github: SiGithub,
  gitlab: SiGitlab,
  google: SiGoogle,
  trello: SiTrello,
  atlassian: SiAtlassian,
  linear: SiLinear,
  pinterest: SiPinterest,
  bitrix24: Bitrix24Icon,
  toggl: SiToggl,
  internalnote: NotebookIcon,
  internaltodo: CheckCheckIcon,
  hackernews: SiYcombinator,
};

const PROVIDER_ICON_CACHE = new Map<string, string>();

// Muted accent colors for widgets without a brand — used only as a faint glow
// behind the monogram, so tiles read as dark product cards, not rainbow blocks.
const ACCENT_PALETTE = [
  "#38bdf8",
  "#2dd4bf",
  "#a78bfa",
  "#fbbf24",
  "#fb7185",
  "#34d399",
  "#60a5fa",
  "#f472b6",
];

const clamp = (value: number, min: number, max: number) => {
  if (Number.isNaN(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, value));
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const hashString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }

  return Math.abs(hash);
};

const getProviderIconMarkup = ({
  providerKey,
  size,
  color,
}: {
  providerKey: string;
  size: number;
  color: string;
}) => {
  const cacheKey = `${providerKey}:${size}:${color}`;
  const cached = PROVIDER_ICON_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  const IconComponent = PROVIDER_ICON_COMPONENTS[providerKey];
  if (!IconComponent) {
    return "";
  }

  const markup = renderToStaticMarkup(
    createElement(IconComponent, {
      size,
      color,
    }),
  );

  PROVIDER_ICON_CACHE.set(cacheKey, markup);
  return markup;
};

const normalizeProviderKey = (rawValue?: string | null) => {
  if (!rawValue) {
    return null;
  }

  const normalized = rawValue.toLowerCase().replace(/[^a-z0-9]/g, "");
  return PROVIDER_ALIASES[normalized] || null;
};

const getProviderKeyFromElement = (elementName: string): string | null => {
  const details = Elements[elementName]?.details as
    | { integrationsUsed?: string[] }
    | undefined;

  const integrationKey = normalizeProviderKey(details?.integrationsUsed?.[0]);
  if (integrationKey) {
    return integrationKey;
  }

  if (elementName.startsWith("Github")) return "github";
  if (elementName.startsWith("Gitlab")) return "gitlab";
  if (elementName.startsWith("Google")) return "google";
  if (elementName.startsWith("Trello")) return "trello";
  if (elementName.startsWith("Jira")) return "atlassian";
  if (elementName.startsWith("Linear")) return "linear";
  if (elementName.startsWith("Pinterest")) return "pinterest";
  if (elementName.startsWith("Bitrix24")) return "bitrix24";
  if (elementName.startsWith("Toggl")) return "toggl";
  if (elementName.startsWith("Hackernews")) return "hackernews";
  if (elementName === "Notes") return "internalnote";
  if (elementName === "TodoList") return "internaltodo";
  if (elementName === "Calendar") return "google";

  return elementName.toLowerCase();
};

const normalizeConfig = (
  config: unknown,
): Record<string, unknown> | undefined => {
  if (!config) {
    return undefined;
  }

  if (typeof config === "string") {
    try {
      const parsed = JSON.parse(config);
      if (parsed && typeof parsed === "object") {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }

    return undefined;
  }

  if (typeof config === "object") {
    return config as Record<string, unknown>;
  }

  return undefined;
};

const getDefaultGridSize = (elementName: string, sizeKey?: string) => {
  const details = Elements[elementName]?.details;
  const grid = details?.grid;

  if (!grid || !Object.keys(grid).length) {
    return { w: 3, h: 2 };
  }

  const preferredSize = (sizeKey && grid[sizeKey]) ||
    Object.values(grid)[0] || [3, 2];
  const [w, h] = preferredSize as number[];

  return {
    w: clamp(Math.round(w || 3), 1, 8),
    h: clamp(Math.round(h || 2), 1, 6),
  };
};

const normalizeGrid = (
  gridValue: unknown,
  fallback: { w: number; h: number },
): { x: number | null; y: number | null; w: number; h: number } => {
  if (Array.isArray(gridValue)) {
    const [w, h, x = 0, y = 0] = gridValue;
    if (
      [w, h, x, y].every(
        value => typeof value === "number" && Number.isFinite(value),
      )
    ) {
      return {
        x: Math.round(x),
        y: Math.round(y),
        w: clamp(Math.round(w), 1, 8),
        h: clamp(Math.round(h), 1, 6),
      };
    }
  }

  if (gridValue && typeof gridValue === "object") {
    const record = gridValue as Record<string, unknown>;
    const x = Number(record.x);
    const y = Number(record.y);
    const w = Number(record.w);
    const h = Number(record.h);

    if ([x, y, w, h].every(value => Number.isFinite(value))) {
      return {
        x: Math.round(x),
        y: Math.round(y),
        w: clamp(Math.round(w), 1, 8),
        h: clamp(Math.round(h), 1, 6),
      };
    }
  }

  return {
    x: null,
    y: null,
    w: fallback.w,
    h: fallback.h,
  };
};

const isIgnoredElement = (elementName: string) => {
  if (BASE_ELEMENT_NAMES.has(elementName)) {
    return true;
  }

  if (LAYOUT_ELEMENT_NAMES.has(elementName)) {
    return true;
  }

  const details = Elements[elementName]?.details;
  const category = details?.category;

  return category === "Layout" || category === "Container";
};

const shortenWidgetName = (name: string) =>
  name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .slice(0, 2)
    .join(" ");

const getWidgetBadge = (name: string) => {
  const label = shortenWidgetName(name).trim();
  if (!label.length) {
    return "WD";
  }

  const parts = label.split(" ");
  if (parts.length > 1) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return label.slice(0, 2).toUpperCase();
};

const collectSnapshotWidgets = (nodes: TemplateSnapshotElement[]) => {
  const widgets: ThumbnailWidget[] = [];
  let order = 0;

  const walk = (currentNodes: TemplateSnapshotElement[]) => {
    currentNodes.forEach(node => {
      const config = normalizeConfig(node.config);
      const fallback = getDefaultGridSize(
        node.name,
        typeof config?.size === "string" ? config.size : undefined,
      );
      const grid = normalizeGrid(config?.grid, fallback);

      if (!isIgnoredElement(node.name)) {
        widgets.push({
          name: node.name,
          x: grid.x,
          y: grid.y,
          w: grid.w,
          h: grid.h,
          providerKey: getProviderKeyFromElement(node.name),
          order,
        });
        order += 1;
      }

      if (node.children?.length) {
        walk(node.children);
      }
    });
  };

  walk(nodes || []);
  return widgets;
};

const collectElementWidgets = (elements: Element[]) => {
  const widgets: ThumbnailWidget[] = [];

  elements.forEach((element, index) => {
    if (isIgnoredElement(element.name)) {
      return;
    }

    const config = normalizeConfig(element.config);
    const fallback = getDefaultGridSize(
      element.name,
      typeof config?.size === "string" ? config.size : undefined,
    );
    const grid = normalizeGrid(config?.grid, fallback);

    widgets.push({
      name: element.name,
      x: grid.x,
      y: grid.y,
      w: grid.w,
      h: grid.h,
      providerKey: getProviderKeyFromElement(element.name),
      order: index,
    });
  });

  return widgets;
};

const normalizeWidgetLayout = (widgets: ThumbnailWidget[]) => {
  let autoIndex = 0;

  const normalized = widgets.map(widget => {
    const w = clamp(Math.round(widget.w || 3), 1, 8);
    const h = clamp(Math.round(widget.h || 2), 1, 6);

    if (widget.x !== null && widget.y !== null) {
      return {
        ...widget,
        w,
        h,
        x: clamp(Math.round(widget.x), 0, Math.max(0, 12 - w)),
        y: clamp(Math.round(widget.y), 0, 120),
      };
    }

    const baseX = (autoIndex * 3) % 12;
    const baseY = Math.floor((autoIndex * 3) / 12) * 2;
    autoIndex += 1;

    return {
      ...widget,
      w,
      h,
      x: clamp(baseX, 0, Math.max(0, 12 - w)),
      y: baseY,
    };
  });

  const maxX = 12;
  const maxY = normalized.reduce(
    (acc, widget) => Math.max(acc, widget.y + widget.h),
    4,
  );

  return {
    widgets: normalized,
    maxX,
    maxY,
  };
};

// Accent color for a tile's soft glow: the widget's brand color when it maps to
// an integration, otherwise a stable muted accent picked from the palette.
const getTileAccent = (widget: ThumbnailWidget) => {
  if (widget.providerKey && PROVIDER_ICON_META[widget.providerKey]) {
    return PROVIDER_ICON_META[widget.providerKey].icon;
  }
  return ACCENT_PALETTE[hashString(widget.name) % ACCENT_PALETTE.length];
};

// Light brand colors (e.g. GitHub white) need a fainter glow to stay tasteful.
const glowOpacityFor = (hex: string) => {
  const c = hex.replace("#", "");
  if (c.length < 6) {
    return 0.12;
  }
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.7 ? 0.06 : 0.12;
};

const UNIDECK_MARK = `<svg width="15" height="15" viewBox="0 0 24 24" fill="#f8fafc"><rect x="1" y="1" width="9" height="9" rx="2"/><rect x="1" y="13" width="9" height="9" rx="2"/><rect x="13" y="1" width="10" height="6" rx="2"/><rect x="13" y="10" width="10" height="12" rx="2"/></svg>`;

// Render an elegant miniature of a real UniDeck dashboard: an ambient wallpaper
// backdrop, a slim search bar, dark widget cards showing their brand mark on a
// soft brand-tinted glow, and a floating user-bar pill that carries the title.
const renderThumbnailSvg = ({
  title,
  widgets,
}: {
  title?: string;
  subtitle?: string;
  widgets: ThumbnailWidget[];
}) => {
  const width = 640;
  const height = 360;
  const padding = 20;
  const contentX = 24;
  const contentY = 58;
  const contentWidth = width - contentX * 2;
  const barTop = 312;
  const contentHeight = barTop - contentY - 8;
  const gap = 5;

  const displayedWidgets = widgets.slice(0, 14);
  const hiddenWidgetsCount = Math.max(
    0,
    widgets.length - displayedWidgets.length,
  );
  const layout = normalizeWidgetLayout(displayedWidgets);
  const xUnit = contentWidth / layout.maxX;
  // Cap row height relative to column width so short dashboards keep pleasant
  // proportions instead of stretched tiles; the block is then vertically centred.
  const yUnit = Math.min(contentHeight / layout.maxY, xUnit * 1.4);
  const gridTop = contentY + Math.max(0, (contentHeight - yUnit * layout.maxY) / 2);

  const glowDefs = layout.widgets
    .map((widget, index) => {
      const accent = getTileAccent(widget);
      const opacity = glowOpacityFor(accent);
      return `<radialGradient id="tglow${index}" cx="50%" cy="40%" r="52%"><stop offset="0%" stop-color="${accent}" stop-opacity="${opacity}" /><stop offset="100%" stop-color="${accent}" stop-opacity="0" /></radialGradient>`;
    })
    .join("");

  const tiles = layout.widgets
    .map((widget, index) => {
      const x = contentX + (widget.x || 0) * xUnit + gap;
      const y = gridTop + (widget.y || 0) * yUnit + gap;
      const w = Math.max(30, widget.w * xUnit - gap * 2);
      const h = Math.max(30, widget.h * yUnit - gap * 2);
      const r = clamp(Math.min(w, h) * 0.16, 8, 14);
      const cx = x + w / 2;
      const accent = getTileAccent(widget);
      const label = escapeXml(shortenWidgetName(widget.name));
      const showLabel = h > 52 && w > 66;
      const glyphY = showLabel ? y + h / 2 - 8 : y + h / 2;

      const iconComponent = widget.providerKey
        ? PROVIDER_ICON_COMPONENTS[widget.providerKey]
        : null;

      let glyph: string;
      if (iconComponent && widget.providerKey) {
        const iconSize = clamp(Math.round(Math.min(w, h) * 0.34), 20, 40);
        const iconColor =
          PROVIDER_ICON_META[widget.providerKey]?.icon || accent;
        const iconMarkup = getProviderIconMarkup({
          providerKey: widget.providerKey,
          size: iconSize,
          color: iconColor,
        });
        glyph = `<g transform="translate(${cx - iconSize / 2}, ${glyphY - iconSize / 2})">${iconMarkup}</g>`;
      } else {
        const badge = escapeXml(getWidgetBadge(widget.name));
        const bSize = clamp(Math.round(Math.min(w, h) * 0.42), 26, 40);
        const bx = cx - bSize / 2;
        const by = glyphY - bSize / 2;
        glyph = `<rect x="${bx}" y="${by}" width="${bSize}" height="${bSize}" rx="${Math.round(
          bSize * 0.28,
        )}" fill="${accent}" fill-opacity="0.14" stroke="${accent}" stroke-opacity="0.34" />
  <text x="${cx}" y="${by + bSize * 0.66}" text-anchor="middle" font-family="Montserrat, sans-serif" font-weight="700" font-size="${Math.round(
    bSize * 0.4,
  )}" fill="${accent}">${badge}</text>`;
      }

      const labelMarkup = showLabel
        ? `<text x="${cx}" y="${y + h - 12}" text-anchor="middle" font-family="Lato, sans-serif" font-size="10.5" fill="#cbd5e1" fill-opacity="0.72">${label}</text>`
        : "";

      return `<g filter="url(#tileShadow)">
  <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="#0b0c10" fill-opacity="0.94" stroke="#ffffff" stroke-opacity="0.08" />
  <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="url(#tglow${index})" />
  ${glyph}
  ${labelMarkup}
</g>`;
    })
    .join("");

  const safeTitle = escapeXml((title || "Dashboard Template").slice(0, 40));
  const approxTitleWidth = safeTitle.length * 7.2;
  const barW = clamp(96 + approxTitleWidth, 220, width - 120);
  const barX = (width - barW) / 2;
  const barH = 34;
  const barMidY = barTop + barH / 2;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
<defs>
  <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
    <stop offset="0%" stop-color="#0c1016" />
    <stop offset="50%" stop-color="#0a0c11" />
    <stop offset="100%" stop-color="#0c0f17" />
  </linearGradient>
  <radialGradient id="ambientA" cx="12%" cy="6%" r="55%">
    <stop offset="0%" stop-color="#5e6b8c" stop-opacity="0.18" />
    <stop offset="100%" stop-color="#5e6b8c" stop-opacity="0" />
  </radialGradient>
  <radialGradient id="ambientB" cx="95%" cy="98%" r="60%">
    <stop offset="0%" stop-color="#7c6a9c" stop-opacity="0.16" />
    <stop offset="100%" stop-color="#7c6a9c" stop-opacity="0" />
  </radialGradient>
  <radialGradient id="vignette" cx="50%" cy="46%" r="75%">
    <stop offset="55%" stop-color="#000000" stop-opacity="0" />
    <stop offset="100%" stop-color="#000000" stop-opacity="0.45" />
  </radialGradient>
  <filter id="tileShadow" x="-20%" y="-20%" width="140%" height="150%">
    <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="#000000" flood-opacity="0.45" />
  </filter>
  <filter id="barShadow" x="-20%" y="-40%" width="140%" height="220%">
    <feDropShadow dx="0" dy="6" stdDeviation="10" flood-color="#000000" flood-opacity="0.55" />
  </filter>
  ${glowDefs}
</defs>
<rect x="0" y="0" width="${width}" height="${height}" rx="22" fill="url(#bg)" />
<rect x="0" y="0" width="${width}" height="${height}" rx="22" fill="url(#ambientA)" />
<rect x="0" y="0" width="${width}" height="${height}" rx="22" fill="url(#ambientB)" />
<rect x="0" y="0" width="${width}" height="${height}" rx="22" fill="url(#vignette)" />
<g>
  <rect x="${padding}" y="18" width="${width - padding * 2}" height="26" rx="13" fill="#ffffff" fill-opacity="0.05" stroke="#ffffff" stroke-opacity="0.09" />
  <circle cx="${padding + 18}" cy="31" r="4.5" fill="none" stroke="#94a3b8" stroke-opacity="0.7" stroke-width="1.6" />
  <line x1="${padding + 21.5}" y1="34.5" x2="${padding + 24}" y2="37" stroke="#94a3b8" stroke-opacity="0.7" stroke-width="1.6" stroke-linecap="round" />
  <rect x="${padding + 32}" y="29" width="150" height="4" rx="2" fill="#94a3b8" fill-opacity="0.32" />
</g>
${tiles}
${
  hiddenWidgetsCount > 0
    ? `<text x="${contentX}" y="${height - 14}" font-family="Lato, sans-serif" font-size="10.5" fill="#94a3b8" fill-opacity="0.75">+${hiddenWidgetsCount} more</text>`
    : ""
}
<g filter="url(#barShadow)">
  <rect x="${barX}" y="${barTop}" width="${barW}" height="${barH}" rx="17" fill="#0b0d12" fill-opacity="0.92" stroke="#ffffff" stroke-opacity="0.10" />
  <g transform="translate(${barX + 15}, ${barTop + 9.5})">${UNIDECK_MARK}</g>
  <line x1="${barX + 40}" y1="${barTop + 10}" x2="${barX + 40}" y2="${barTop + barH - 10}" stroke="#ffffff" stroke-opacity="0.14" />
  <text x="${barX + 50}" y="${barMidY + 4.5}" font-family="Montserrat, sans-serif" font-weight="700" font-size="13" fill="#f8fafc">${safeTitle}</text>
  <circle cx="${barX + barW - 20}" cy="${barMidY}" r="2" fill="#94a3b8" fill-opacity="0.7" />
  <circle cx="${barX + barW - 12}" cy="${barMidY}" r="2" fill="#94a3b8" fill-opacity="0.45" />
</g>
</svg>`;
};

const svgToDataUri = (svg: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

export const getTemplateWidgetBreakdownFromSnapshot = (
  elements: TemplateSnapshotElement[],
): WidgetBreakdown[] => {
  const counts = new Map<string, number>();
  const widgets = collectSnapshotWidgets(elements || []);

  widgets.forEach(widget => {
    counts.set(widget.name, (counts.get(widget.name) || 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([nameA, countA], [nameB, countB]) => {
      if (countA === countB) {
        return nameA.localeCompare(nameB);
      }

      return countB - countA;
    })
    .map(([name, count]) => ({ name, count }));
};

export const getTemplateWidgetBreakdownFromElements = (
  elements: Element[],
): WidgetBreakdown[] => {
  const counts = new Map<string, number>();
  const widgets = collectElementWidgets(elements || []);

  widgets.forEach(widget => {
    counts.set(widget.name, (counts.get(widget.name) || 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([nameA, countA], [nameB, countB]) => {
      if (countA === countB) {
        return nameA.localeCompare(nameB);
      }

      return countB - countA;
    })
    .map(([name, count]) => ({ name, count }));
};

export const generateTemplateThumbnailFromSnapshot = ({
  elements,
  title,
  subtitle,
}: {
  elements: TemplateSnapshotElement[];
  title?: string;
  subtitle?: string;
}) => {
  const widgets = collectSnapshotWidgets(elements || []);
  const svg = renderThumbnailSvg({
    widgets,
    title,
    subtitle,
  });

  return svgToDataUri(svg);
};

export const generateTemplateThumbnailFromElements = ({
  elements,
  title,
  subtitle,
}: {
  elements: Element[];
  title?: string;
  subtitle?: string;
}) => {
  const widgets = collectElementWidgets(elements || []);
  const svg = renderThumbnailSvg({
    widgets,
    title,
    subtitle,
  });

  return svgToDataUri(svg);
};
